import { Helmet } from "react-helmet";
import { useQuery } from "@apollo/client";
import { RequestFailed } from "src/components/RequestFailed";
import {
	getCoreRowModel,
	getPaginationRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { ControlledDataTable } from "@/components/ui/controlled-data-table";
import { Skeleton } from "@chakra-ui/react";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button as UIButton } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { communityAnnouncementsColumns } from "@/features/communities/columns";
import { GET_COMMUNITY_ANNOUNCEMENTS } from "src/features/communities/graphql";

export default function Communities() {
	const {
		loading: loadingAnnouncements,
		data: announcementsData,
		error: announcementsError,
	} = useQuery(GET_COMMUNITY_ANNOUNCEMENTS, {
		fetchPolicy: "network-only",
	});

	const announcements =
		announcementsData?.getCommunityAnnouncementsForPro || [];

	const announcementsTable = useReactTable({
		data: announcements,
		columns: communityAnnouncementsColumns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	if (announcementsError) {
		return <RequestFailed text={announcementsError.message} />;
	}

	return (
		<>
			<Helmet>
				<title>LaborHack | Communities</title>
			</Helmet>
			<main className="flex flex-col gap-8">
				<div className="flex flex-col gap-3 lg:gap-6">
					<div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 border-b pb-2">
						<h1 className="text-[18px] lg:text-[23px] font-semibold">
							Communities
						</h1>
					</div>

					{/* Announcements Section */}
					<div className="">
						<h2 className="text-[15px] lg:text-[18px] font-semibold mb-4">
							Announcements
						</h2>
						<div className="flow-root">
							{loadingAnnouncements ? (
								<div className="rounded-md border">
									<div className="p-4 space-y-4">
										{/* Header skeleton */}
										<div className="flex border-b pb-4">
											{[1, 2, 3, 4].map((i) => (
												<Skeleton key={i} className="h-5 w-1/4 mr-4" />
											))}
										</div>
										{/* Rows skeleton */}
										{Array(5)
											.fill(0)
											.map((_, index) => (
												<div key={index} className="flex py-3">
													{[1, 2, 3, 4].map((i) => (
														<Skeleton key={i} className="h-5 w-1/4 mr-4" />
													))}
												</div>
											))}
									</div>
								</div>
							) : (
								<>
									<ControlledDataTable table={announcementsTable} />
									<div className="flex flex-col">
										<Separator />
										<div className="w-full min-h-16">
											<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 py-4">
												<p className="flex items-center gap-2 text-sm text-gray-500">
													{announcementsTable.getRowCount() === 0 ? (
														"Showing no announcements"
													) : (
														<>
															Showing
															<span className="text-sm text-[#121212]">
																{announcementsTable.getState().pagination
																	.pageIndex *
																	announcementsTable.getState().pagination
																		.pageSize +
																	1}
																-
																{Math.min(
																	(announcementsTable.getState().pagination
																		.pageIndex +
																		1) *
																		announcementsTable.getState().pagination
																			.pageSize,
																	announcementsTable.getFilteredRowModel().rows
																		.length
																)}
															</span>
															of{" "}
															<span className="text-sm text-[#121212]">
																{announcementsTable.getRowCount()}
															</span>
															announcements
														</>
													)}
												</p>
												<div className="flex items-center gap-2">
													<div className="flex items-center space-x-2">
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!announcementsTable.getCanPreviousPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() => announcementsTable.previousPage()}
															disabled={
																!announcementsTable.getCanPreviousPage()
															}
														>
															<ChevronLeft className="w-4 h-4" />
														</UIButton>
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!announcementsTable.getCanNextPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() => announcementsTable.nextPage()}
															disabled={!announcementsTable.getCanNextPage()}
														>
															<ChevronRight className="w-4 h-4" />
														</UIButton>
													</div>
													<Select
														value={String(
															announcementsTable.getState().pagination.pageSize
														)}
														onValueChange={(value) => {
															announcementsTable.setPageSize(Number(value));
														}}
													>
														<SelectTrigger className="w-[70px]">
															<SelectValue placeholder="10" />
														</SelectTrigger>
														<SelectContent>
															{[5, 10, 20, 50].map((pageSize) => (
																<SelectItem
																	key={pageSize}
																	value={String(pageSize)}
																>
																	{pageSize}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</main>
		</>
	);
}
